'use client';

import React, { useMemo } from 'react';
import { useRouter } from 'next/navigation';
import { mutate } from 'swr';
import clsx from 'clsx';
import i18n from '@shared/i18n';
import { useToast } from '@shared/ui';
import { postCart } from 'apis/cart';
import { useBuyerProductStore } from 'app/store/product';
import { CACHE_QUERY_KEY } from 'app/constants';
import { TRACK_EVENT, logEvent } from 'app/libs/track';
import STYLE from './ProductInfoGroup.module.scss';

interface ProductCartButtonsProps {
    type: string;
}

export const ProductCartButtons: React.FC<ProductCartButtonsProps> = (props) => {
    const { type } = props;

    const router = useRouter();

    const toast = useToast();

    const { productId, productQuantity, selectedProductDetailId, product } =
        useBuyerProductStore();

    const outOfStock = useMemo(() => {
        if (
            !product ||
            (typeof product?.total_stock === 'number' && product?.total_stock <= 0)
        ) {
            return true;
        }

        const chooseSpecDetail = product.spec_details.find(
            (i) => i.id === selectedProductDetailId
        );

        return chooseSpecDetail && chooseSpecDetail.stock <= 0;
    }, [product, selectedProductDetailId]);

    const handleAddToCart = (goToCart: boolean) => {
        postCart({
            product_id: productId,
            quantity: productQuantity,
            ...(selectedProductDetailId
                ? { spec_detail_id: selectedProductDetailId }
                : {}),
        }).then(() => {
            mutate(CACHE_QUERY_KEY.buyerCartCount);

            logEvent(TRACK_EVENT.ADD_TO_CART, {
                event: TRACK_EVENT.ADD_TO_CART,
            });

            toast({
                title: i18n.gettext('已加入購物車'),
                status: 'success',
            });
        });

        if (goToCart) {
            router.push('/cart');
        }
    };

    const isNotLaunchedYet = useMemo(() => {
        if (!product || !product.launch_datetime) {
            return false;
        }

        const now = Date.now();

        return product.launch_datetime * 1000 > now;
    }, [product]);

    return (
        <div
            className={clsx(
                STYLE.buttonsBox,
                type === 'feature' && STYLE.featureButtonBox,
                outOfStock && STYLE.outOfStock
            )}
        >
            {!isNotLaunchedYet && (
                <button
                    className={clsx(STYLE.button, STYLE.positiveButton)}
                    onClick={() => handleAddToCart(false)}
                >
                    {outOfStock ? i18n.gettext('已售完') : i18n.gettext('加入購物車')}
                </button>
            )}

            {!outOfStock && (
                <button
                    disabled={isNotLaunchedYet}
                    className={clsx(STYLE.button, STYLE.negativeButton)}
                    onClick={() => handleAddToCart(true)}
                >
                    {isNotLaunchedYet
                        ? i18n.gettext('尚未販售')
                        : i18n.gettext('直接購買')}
                </button>
            )}
        </div>
    );
};
